
import React from "react";
import { useTranslation } from "react-i18next";
import { getReadinessColor } from "../../utils/readinessUtils";
import DonutChartCard from "../DonutChartCard";
import SummaryBox from "../SummaryBox";
import OverallDonut from "../OverallDonut";
import DetailReportTable from "./DetailReportTable";
import {
    categoryLabels,
    getCategoryLabel,
    getSubcategoryLabel,
} from "../../utils/categoryLabels";
import {
    FaBug,
    FaExclamationTriangle,
    FaExchangeAlt,
    FaFileUpload,
    FaRobot,
    FaBalanceScale,
    FaNetworkWired,
    FaBolt,
    FaClock,
    FaFolderOpen,
    FaUserShield,
    FaHeartbeat,
    FaCreditCard,
    FaLandmark,
    FaShieldAlt,
    FaClipboardCheck,
    FaCertificate,
    FaUserSecret,
} from "react-icons/fa";
import { AiFillSecurityScan } from "react-icons/ai";

const categoryIcons = {
    "behavioural_threats": <FaExclamationTriangle className="text-yellow-500 text-xl mr-4" />,
    "input_output_risks": <FaExchangeAlt className="text-blue-500 text-xl mr-4" />,
    "malicious_doc_uploads": <FaFileUpload className="text-pink-500 text-xl mr-4" />,
    "llm_api_mis_use_abuse": <FaRobot className="text-green-500 text-xl mr-4" />,
    "policy_compliance_tests": <FaBalanceScale className="text-purple-500 text-xl mr-4" />,
    "llm_dos": <FaBolt className="text-amber-500 text-xl mr-4" />,
    "mcp_agent": <FaNetworkWired className="text-orange-500 text-xl mr-4" />,
    "rc_gdpr": <FaUserShield className="text-blue-500 text-xl mr-4" />,
    "rc_hipaa": <FaHeartbeat className="text-red-500 text-xl mr-4" />,
    "rc_pci_dss": <FaCreditCard className="text-green-500 text-xl mr-4" />,
    "rc_eu_ai_act": <FaLandmark className="text-indigo-500 text-xl mr-4" />,
    "rc_owasp_llm": <FaShieldAlt className="text-orange-500 text-xl mr-4" />,
    "rc_nist_ai_rmf": <FaClipboardCheck className="text-teal-500 text-xl mr-4" />,
    "rc_soc2": <FaCertificate className="text-purple-500 text-xl mr-4" />,
    "prompt_injection": <FaUserSecret className="text-rose-500 text-xl mr-4" />,
};

const getCategoryKey = (displayLabel) => {
    return Object.keys(categoryLabels).find(key => categoryLabels[key] === displayLabel) || null;
};

const getCountTone = (count) => {
    if (typeof count !== "number") return "muted";
    return count === 0 ? "ok" : "bad";
};

const CategoryCountChips = ({ items }) => {
    if (!items.length) return null;

    return (
        <div className="report-count-chips">
            {items.map(({ key, label, value, tone }) => (
                <span key={key} className="report-count-chip">
                    {label}
                    <strong className={`report-count-chip-value is-${tone}`}>{value}</strong>
                </span>
            ))}
        </div>
    );
};

const SummaryComponent = ({ overallReadiness, donutData, categoryStats, summaryData, stats, data, reportData }) => {
    const { t } = useTranslation("reports_recs", { keyPrefix: "reports_common" });
    const rawQuickStats = reportData?.score_json?.quick_stats || {};
    const getOrNA = (v) => (v === undefined || v === null ? "N/A" : v);
    const displayOrNA = (v) => (v === "N/A" ? t("na") : v);
    const quickStats = {
        total_tests: getOrNA(rawQuickStats.total_tests),
        total_categories_tested: getOrNA(rawQuickStats.total_categories_tested),
        total_high_risk: getOrNA(rawQuickStats.total_high_risk),
        total_failed_tests: getOrNA(rawQuickStats.total_failed_tests),
        overall_avg_response_time: getOrNA(rawQuickStats.overall_avg_response_time),
    };

    // Define the order of categories for the stats cards
    const categoryKeys = [
        "malicious_doc_uploads",
        "llm_api_mis_use_abuse",
        "policy_compliance_tests",
        "llm_dos",
        "mcp_agent",
        "behavioural_threats",
        "input_output_risks",
        "prompt_injection",
        "rc_gdpr",
        "rc_hipaa",
        "rc_pci_dss",
        "rc_eu_ai_act",
        "rc_owasp_llm",
        "rc_nist_ai_rmf",
        "rc_soc2",
    ];

    // Helper function to get failed test count from API data
    const getFailedTestCount = (categoryKey) => {
        const categoryData = reportData?.score_json?.data?.[categoryKey];

        if (!categoryData) {
            return "N/A";
        }

        return categoryData.total_failed || 0;
    };

    const apiErrorCount = rawQuickStats.api_error_count && typeof rawQuickStats.api_error_count === "object"
        ? rawQuickStats.api_error_count
        : null;
    const apiErrorCategoryKeys = apiErrorCount
        ? [
            ...categoryKeys.filter((key) => apiErrorCount[key] !== undefined && apiErrorCount[key] !== null),
            ...Object.keys(apiErrorCount).filter((key) => !categoryKeys.includes(key)),
          ]
        : [];

    const getApiErrorCount = (categoryKey) => {
        if (!apiErrorCount || apiErrorCount[categoryKey] === undefined || apiErrorCount[categoryKey] === null) {
            return "N/A";
        }
        return apiErrorCount[categoryKey];
    };

    const failedToBlockItems = categoryKeys
        .map((categoryKey) => {
            const value = getFailedTestCount(categoryKey);
            if (value === "N/A") return null;
            return {
                key: categoryKey,
                label: getCategoryLabel(categoryKey),
                value,
                tone: getCountTone(value),
            };
        })
        .filter(Boolean);

    const apiErrorItems = apiErrorCategoryKeys.map((categoryKey) => {
        const value = getApiErrorCount(categoryKey);
        return {
            key: categoryKey,
            label: getCategoryLabel(categoryKey),
            value: displayOrNA(value),
            tone: getCountTone(value),
        };
    });

    return (
        <>
            {/* Donut charts */}
            <div className="grid grid-4">
                {/* Box 1 - Overall Readiness */}
                <SummaryBox title={t("overallReadiness")}>

                    <OverallDonut title="" value={overallReadiness} />
                    <p style={{ textAlign: "center", color: "var(--body-text)" }}>{t("overallReadinessDescription")}</p>
                </SummaryBox>

                {/* Box 2 - Category Donut */}
                <SummaryBox title={t("categoryWiseReadiness")}>
                    <DonutChartCard title="" data={donutData} />
                </SummaryBox>

                {/* Box 3 - Category Stats */}
                <SummaryBox title={t("categoryDetails")}>
                    <div className="space-y-4">
                        {categoryStats.filter(c => {
                            const key = getCategoryKey(c.category);
                            return key && reportData?.score_json?.data?.[key];
                        }).map((c, i) => (
                            <div key={i}>
                                <div className="flex items-center justify-start">
                                    {categoryIcons[getCategoryKey(c.category)] || <FaFolderOpen className="text-gray-500 text-xl mr-4" />}
                                    <span className="flex-1 ml-2">
                                        {c.category}: <span className="font-bold" style={{ color: getReadinessColor(c.readiness) }}>{c.readiness.toFixed(1)}%</span>
                                    </span>
                                </div>
                                <hr className="summary-separator" />
                            </div>
                        ))}
                    </div>
                </SummaryBox>


                {/* Box 4 - Quick Stats */}
                <SummaryBox title={t("quickStats")}>
                    <div className="space-y-4">

                       
                        <div className="flex items-center justify-start">
                            <AiFillSecurityScan className="text-red-600 text-xl mr-4" />
                            <span className="flex-1" style={{ marginLeft: '8px' }}>
                                {t("totalScans")}: <span className="font-bold">{displayOrNA(quickStats.total_tests)}</span>
                            </span>
                        </div>
                        <hr className="summary-separator" />

                        <div className="flex items-center justify-start">
                            <FaFolderOpen className="text-pink-500 text-xl mr-4" />
                            <span className="flex-1" style={{ marginLeft: '8px' }}>
                                {t("categories")}: <span className="font-bold">{displayOrNA(quickStats.total_categories_tested)}</span>
                            </span>
                        </div>
                        <hr className="summary-separator" />

                        <div className="flex items-center justify-start">
                            <FaExclamationTriangle className="text-red-600 text-xl mr-4" />
                            <span className="flex-1" style={{ marginLeft: '8px' }}>
                                {t("highRiskItems")}: <span className="font-bold" style={{ color: '#ef4444' }}>{displayOrNA(quickStats.total_high_risk)}</span>
                            </span>
                        </div>
                        <hr className="summary-separator" />

                        <div className="flex items-center justify-start">
                            <FaBug className="text-red-500 text-xl mr-4" />
                            <span className="flex-1" style={{ marginLeft: '8px' }}>
                                {t("failedToBlock")}: <span className="font-bold" style={{ color: '#ef4444' }}>{displayOrNA(quickStats.total_failed_tests)}</span>
                            </span>
                        </div>
                        <hr className="summary-separator" />

                        <div className="flex items-center justify-start">
                            <FaClock className="text-yellow-400 text-xl mr-4" />
                            <span className="flex-1" style={{ marginLeft: '8px' }}>
                                {t("avgResponseTime")}: <span className="font-bold">{displayOrNA(quickStats.overall_avg_response_time)}{quickStats.overall_avg_response_time !== "N/A" ? t("secondsUnit") : ""}</span>
                            </span>
                        </div>

                    </div>
                </SummaryBox>
            </div>


            <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", alignItems: "stretch" }}>
                <div style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
                    <h3>{t("failedToBlock")}</h3>
                    <div
                        className="security-container"
                        style={{
                            flex: 1,
                            display: "flex",
                            alignItems: failedToBlockItems.length > 0 ? "flex-start" : "center",
                            justifyContent: "flex-start",
                        }}
                    >
                        {failedToBlockItems.length > 0 ? (
                            <CategoryCountChips items={failedToBlockItems} />
                        ) : (
                            <span style={{ color: "var(--body-text)", opacity: 0.6, fontSize: "13px" }}>{t("noData")}</span>
                        )}
                    </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
                    <h3>{t("apiErrorCounts")}</h3>
                    <div
                        className="security-container"
                        style={{
                            flex: 1,
                            display: "flex",
                            alignItems: apiErrorItems.length > 0 ? "flex-start" : "center",
                            justifyContent: "flex-start",
                        }}
                    >
                        {apiErrorItems.length > 0 ? (
                            <CategoryCountChips items={apiErrorItems} />
                        ) : (
                            <span style={{ color: "var(--body-text)", opacity: 0.6, fontSize: "13px" }}>{t("noData")}</span>
                        )}
                    </div>
                </div>
            </div>

            <h3>{t("securityStatus")}</h3>
            <div className="security-container" style={{ overflowX: "hidden" }}>
                {(() => {
                    // Build flattened rows from score_json, only showing categories and subcategories that exist in the response
                    const scoreJson = reportData?.score_json || {};
                    const categoriesData = scoreJson.data || {};

                    const detailsRows = Object.keys(categoriesData).flatMap((categoryKey) => {
                        const catData = categoriesData[categoryKey] || {};
                        const subReadiness = catData.sub_cat_readiness || {};
                        const subAvgTime = catData.sub_cat_avg_time || {};
                        const subCatCounts = catData.sub_cat_counts || {};
                        const displayCategory = getCategoryLabel(categoryKey);

                        // Only show subcategories that exist in sub_cat_readiness
                        return Object.keys(subReadiness).map((sub) => ({
                            category: displayCategory,
                            subcategory: getSubcategoryLabel(categoryKey, sub),
                            readiness: Math.round(typeof subReadiness[sub] === "number" ? subReadiness[sub] : 0),
                            totalScans: subCatCounts?.total_tests?.[sub] || 0,
                            scanPassed: subCatCounts?.total_passed?.[sub] || 0,
                            scanFailed: subCatCounts?.total_failed?.[sub] || 0,
                            averageResponseTime: typeof subAvgTime[sub] === "number" ? Math.round(subAvgTime[sub] * 1000) / 1000 : 0,
                        }));
                    });

                    return <DetailReportTable data={detailsRows} className="security-table" />;
                })()}
            </div>
        </>
    );
};

export default SummaryComponent;
